import * as Yup from 'yup';
import { useWatch } from 'react-hook-form';
import { Box } from '@mui/material';

import formatPhoneNumber from 'src/compass/shared/util/formatPhoneNumber';

import {
  CasDisplayField,
  CasEditForm,
  CasMultiRowTable,
  CasRHFSelect,
  CasRHFSwitch,
  IEditForm,
} from 'src/compass/shared/components';
// Direct path rather than the application barrel: this file is inside it.
import useFeatureFlags from 'src/compass/application/general/hooks/useFeatureFlags';
import {
  contactMethods,
  contactPhones,
  isInvalidAddress,
  phoneDigits,
  preferredPhoneDigits,
} from './contactPhones';
import ContactDetailsButton from './ContactDetailsButton';
import { InvalidAddressChip } from './InvalidAddressChip';

/*
  Edit the two things SCE Reports owns about a customer's contacts.

  The contacts themselves are merged from HubSpot (stg.spMergeEntityContacts), so they can be
  neither added nor removed here - hence CasMultiRowTable's fixedRows, which hides the add and
  delete controls while leaving the inputs editable. The four identity columns are shown read-only
  so the sender can see who they are about to include.

  A contact's name, phones and emails belong to HubSpot, not to this dialog, but the pencil in each
  Name cell opens Contact Details (ContactDetailsButton), which edits them there and puts
  HubSpot's result back into the row.
*/

/*
  Which way a preferred channel reaches someone. Mobilephone and Phone are both phone numbers, so
  they count as the SAME channel and a default set may mix them freely. Email cannot mix with
  either - a message goes out one way or the other, so the odd one out would be dropped silently.
  An additional email the contact prefers is held as the address itself (see preferredEmail), so
  an @ means email too.
*/
const channelOf = (preferredChannel?: string | null) => {
  if (preferredChannel === 'Email' || preferredChannel?.includes('@')) return 'email';
  return preferredChannel ? 'phone' : null;
};

const PREFERRED_COLUMNS = ['', 'Email', 'Mobilephone', 'Phone'];

const includedByDefault = (contacts?: any[]): any[] =>
  (contacts ?? []).filter((c) => c?.defaultInclude);

/* The channel the set has settled on - the one its rows that name a channel agree upon. */
const setChannelOf = (included: any[]): string | null =>
  included.map((c: any) => channelOf(c.preferredChannel)).find(Boolean) ?? null;

/*
  How a contact is reached on a channel, or nothing. Either phone column answers for "phone":
  Mobilephone and Phone are the same channel, so holding either is enough.
*/
const addressOnChannel = (contact: any, channel: string | null) =>
  channel === 'email' ? contact.email : contact.mobilephone || contact.phone;

const Schema = Yup.object().shape({
  contacts: Yup.array()
    .of(
      Yup.object().shape({
        defaultInclude: Yup.boolean(),
        // The select only ever offers a channel the contact has an address for, so this guards
        // the request rather than the user - a stale dialog whose contact lost its mobile. A
        // column name, or one of the contact's own additional emails.
        preferredChannel: Yup.string()
          .nullable()
          .test(
            'on-file',
            'Pick one of the addresses on file for this contact',
            function onFile(value) {
              return (
                value == null ||
                PREFERRED_COLUMNS.includes(value) ||
                (this.parent?.additionalEmails ?? []).includes(value)
              );
            }
          ),
      })
    )
    /*
      No preferred channel is needed: a default set where nobody names one is reached on whichever
      channel the send is on (story 15823). Those that do name one have to agree, and the rest
      follow it, provided they are reachable that way (second test below).
    */
    .test(
      'defaults-share-a-channel',
      'Contacts included by default must all use email or all use phone - they cannot be mixed',
      (contacts?: any[]) => {
        const channels = new Set(
          includedByDefault(contacts)
            .map((c: any) => channelOf(c.preferredChannel))
            .filter(Boolean)
        );
        return channels.size <= 1;
      }
    )
    /*
      A contact left blank follows the set's channel, so it has to be reachable on it. Checked
      here as well as on the server because this is where it can be acted on - the offending
      contact is on screen, and the fix is to give them a channel or to stop including them.
    */
    .test('defaults-reachable-on-channel', 'placeholder', function reachable(contacts?: any[]) {
      const included = includedByDefault(contacts);
      const setChannel = setChannelOf(included);
      if (!setChannel) return true;

      const unreachable = included
        .filter((c: any) => !c.preferredChannel && !addressOnChannel(c, setChannel))
        .map((c: any) => c.name);

      if (unreachable.length === 0) return true;

      const isEmail = setChannel === 'email';
      return this.createError({
        message:
          `${unreachable.join(', ')} ${unreachable.length > 1 ? 'have' : 'has'} no ` +
          `${isEmail ? 'email address' : 'phone number'}, so cannot be included by default ` +
          `with contacts sent by ${isEmail ? 'email' : 'text'}`,
      });
    }),
});

/*
  One option per address the contact actually has; an empty column is not offered. In the order
  the Email/Phone cell beside it lists them (see contactMethods), with its numbers de-duped and
  formatted the same way - a number held in both phone columns is one choice, not two.

  When the saved channel is the column the de-dupe dropped (a Phone that repeats the Mobilephone),
  the number's one option carries the saved value, so the select opens on it rather than blank and
  the record stays as it was. Matched by digits, as the Contacts tab places its Preferred marker.

  The main email is offered as the Email column, as before; each additional email after it as its
  own address, which is what the server stores the choice by (see preferredEmail). The main email
  says (primary), as each number says which column it came from; the additional ones say nothing,
  as in the Email/Phone cell beside it.

  Watched rather than read, so the options follow a Contact Details save into this row.
*/
function PreferredChannelSelect({ rowNameRef, ...props }: any) {
  const row = useWatch({ name: rowNameRef }) ?? {};
  const preferredDigits = preferredPhoneDigits(row);

  // An address no message can go to says so (story 15723): chosen, it would be no one's default -
  // the sends fall back past it.
  const invalid = (value: string) => (isInvalidAddress(row, value) ? ' - invalid' : '');

  const options = [
    { id: '', name: 'None' },
    ...(row.email ? [{ id: 'Email', name: `${row.email} (primary)${invalid(row.email)}` }] : []),
    ...(row.additionalEmails ?? []).map((address: string) => ({
      id: address,
      name: `${address}${invalid(address)}`,
    })),
    ...contactPhones(row)
      .filter(({ channel }) => channel)
      .map(({ value, channel }) => ({
        id: preferredDigits && phoneDigits(value) === preferredDigits ? row.preferredChannel : channel,
        name: `${formatPhoneNumber(value)} (${channel === 'Mobilephone' ? 'Mobile Phone' : 'Phone'})${invalid(value)}`,
      })),
  ];

  return (
    <CasRHFSelect
      {...props}
      options={options}
      // CasRHFSelectBase capitalizes the closed select's text, which renders an address as
      // "Kirkh@Barkcattle.Com". These options ARE addresses, so they have to read verbatim.
      sx={{ '& .MuiSelect-select': { textTransform: 'none' } }}
    />
  );
}

/*
  Every way of reaching the contact in one cell, in contactMethods' order - the emails, then the
  numbers - laid out the way the Customer dialog's Contacts tab lays them out - value then the kind,
  one per line - minus its tel:/sms: links, which have no place in an editor.

  Numbers are de-duped on the last 10 digits, because a contact whose mobile and phone hold the
  same number would otherwise read as two choices. Nothing wraps: an address broken across lines
  is harder to read than one that runs past its column.

  Watched rather than read, like the select, so a Contact Details save shows here at once.
*/
function RowContactMethods({ rowNameRef }: { rowNameRef: string }) {
  const row = useWatch({ name: rowNameRef }) ?? {};
  const methods = contactMethods(row);

  if (!methods.length) return null;

  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      {methods.map((method) =>
        method.kind === 'email' ? (
          <div key={method.value}>
            {method.value} {method.suffix && <i>{method.suffix}</i>}{' '}
            {method.invalid && <InvalidAddressChip kind="email" />}
          </div>
        ) : (
          <div key={method.suffix}>
            {formatPhoneNumber(method.value)} <i>{method.suffix}</i>{' '}
            {method.invalid && <InvalidAddressChip kind="phone" />}
          </div>
        )
      )}
    </Box>
  );
}

/*
  The contact's name, with the pencil at the cell's right edge that opens Contact Details - its
  name, phones and emails, edited in HubSpot - while the Show HubSpot Contact Edit feature flag is
  on for the user. Watched, so a renamed contact reads its new name at once.
*/
function RowName({
  rowNameRef,
  onContactChanged,
}: {
  rowNameRef: string;
  onContactChanged?: VoidFunction;
}) {
  const name = useWatch({ name: `${rowNameRef}.name` }) ?? '';
  const { showHubspotContactEdit } = useFeatureFlags();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box sx={{ flexGrow: 1 }}>{name}</Box>
      {showHubspotContactEdit && (
        <ContactDetailsButton rowNameRef={rowNameRef} onContactChanged={onContactChanged} />
      )}
    </Box>
  );
}

export default function CustomerContactsForm({
  row,
  onSaved,
  editFormAdditionalData,
}: IEditForm) {
  const { insuredName } = editFormAdditionalData;

  return (
    <CasEditForm
      defaultValues={row}
      entity={editFormAdditionalData?.entity ?? 'SceContact'}
      onSaved={(data: any) => onSaved(data.contacts)}
      schema={Schema}
      mode="onSubmit"
    >
      <CasDisplayField label="Customer" value={row?.customerName || insuredName} />
      {/*
        CasMultiRowTable sets verticalAlign: 'super' on every row, which suits a grid of equal
        height inputs but leaves the read-only text sitting at the top of rows made tall by the
        switch and the select - and taller still when a long phone# wraps. Corrected here rather
        than in the component, which SCE Entry shares.
      */}
      <Box sx={{ width: '100%', '& tbody td.MuiTableCell-root': { verticalAlign: 'middle' } }}>
        <CasMultiRowTable
          name="contacts"
          width="100%"
          fixedRows
          columns={[
            {
              type: 'display',
              label: 'Name',
              name: 'name',
              value: ({ rowNameRef }: any) => (
                <RowName
                  rowNameRef={rowNameRef}
                  onContactChanged={editFormAdditionalData?.onContactChanged}
                />
              ),
              align: 'left',
              width: '22%',
            },
            // The two editable columns sit next to Name: they are what this dialog is for, and the
            // identity columns after them are reference, read in passing to confirm the address.
            {
              type: 'component',
              label: 'Include by default on msgs',
              name: 'defaultInclude',
              component: CasRHFSwitch,
              width: '15%',
            },
            {
              type: 'component',
              label: 'Preferred',
              name: 'preferredChannel',
              component: PreferredChannelSelect,
              width: '24%',
            },
            {
              type: 'display',
              label: 'Email/Phone',
              name: 'email',
              value: ({ rowNameRef }: any) => <RowContactMethods rowNameRef={rowNameRef} />,
              align: 'left',
              width: '30%',
            },
          ]}
        />
      </Box>
    </CasEditForm>
  );
}
